import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject
} from '@angular/core';

import { NotificationDrawerComponent } from '../notification-drawer/notification-drawer.component';

/**
 * THE ALERTS, HUNG FROM THE BELL. Walkthrough 09-29 #11, approved by Bethany.
 *
 * The drawer was a full-height 430px panel pinned to the right edge of the
 * window, nowhere near the bell that opened it, and its empty state (which
 * every member sees: nothing writes a notification yet) was three
 * paragraphs. This is the same feed, the same four states, the same dismiss,
 * destination, retry, Escape and focus handling (it IS the drawer's class),
 * drawn as a popover under the bell with a one-line empty state.
 *
 * NotificationDrawerComponent stays in the code for rollback: point the
 * header back at `cpd-notification-drawer` to restore it.
 */
@Component({
  selector: 'cpd-notification-popover',
  templateUrl: './notification-popover.component.html',
  styleUrls: ['./notification-popover.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationPopoverComponent extends NotificationDrawerComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * A click anywhere off the popover closes it, the way a menu does, since
   * there is no scrim any more. Not a click on the bell: the bell's own
   * handler toggles it, and closing here too would reopen it.
   */
  @HostListener('document:click', ['$event'])
  public closeOnClickAway(event: MouseEvent): void {
    if (!this.isOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (
      target &&
      !this.host.nativeElement.contains(target) &&
      !target.closest?.('.alerts-button')
    ) {
      this.close();
    }
  }

  /**
   * 508 audit. ESCAPE FROM ANYWHERE. The panel's own keydown only hears
   * Escape while focus is inside it, so once focus had left, the popover
   * stayed open and the bell kept saying aria-expanded=true. This closes it
   * from wherever focus is, and `closed` resets the bell. An Escape inside
   * the panel never gets here: `onKeydown` stops it.
   *
   * Focus goes back to the bell only when it was in the popover. Otherwise
   * the member has moved on, and Escape should not pull them back.
   */
  @HostListener('document:keydown.escape', ['$event'])
  public closeOnEscape(event?: KeyboardEvent): void {
    // #1852 review. Something closer to the press already handled it (a
    // dialog, a menu, the hover popup): one Escape closes one thing.
    if (!this.isOpen || event?.defaultPrevented) {
      return;
    }
    if (!this.host.nativeElement.contains(document.activeElement)) {
      this.opener = null;
    }
    this.close();
  }
}
